import { ThunkAction } from "redux-thunk";
import {
  IBusinessPolicyMarketingHistory,
  IBusinessPolicyMarketingBinderPdf,
  IBusinessPolicyMatketingAttachment,
  IBusinessBinder,
  IChecklistResponse,
} from "./businessPolicyMarketing.types";
import { IStoreState } from "../../initialStoreState";
import { AnyAction } from "redux";
import { api } from "../../../api/api";
import {
  saveLoaderCompleted,
  saveLoaderProgress,
  showMessage,
} from "../../messages/messagesActions";
import { action } from "typesafe-actions";
import { ERROR_MESSAGES } from "../../../constants/enums";
import { IInsurerUnderWriter } from "../../insurer/insurer.types";
import { IMail } from "../../common/common.types";
import { getUniqueId } from "../../../helpers";
import { IFileUpload } from "../../../features/FileUpload/components/FileUpload.type";
import { uploadMultipleFile } from "../../../features/FileUpload/utils";
import { Buffer } from "buffer";
import { ITemplateField } from "../../CustomFormFields/CustomFormFields.types";
import { buildBinderRequest } from "../leadBinder/leadBinderEndpoints";

// Feature: lead-binder-business-tabs
// Client-side timeout (ms) enforcing the 30-second bound for lead-scoped binder
// marketing-history fetch/save requests (Requirements 3.5, 3.6).
const LEAD_BINDER_REQUEST_TIMEOUT = 30000;

// Feature: lead-binder-business-tabs
// Module-scoped guard preventing a duplicate lead-scoped marketing-history save from
// being dispatched while an earlier save for the lead binder is still in flight
// (Requirement 3.8). The customer-policy save path is intentionally not gated so its
// existing behavior remains byte-for-byte unchanged (Requirement 4.2).
let isLeadMarketingSaveInProgress = false;

export const businessPolicyPreviewMarketingAsync =
  (
    data: IBusinessPolicyMarketingBinderPdf,
    onCallback: (isSuccess: boolean, html?: any) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      const res = await api.post(
        `/policy/market-binder-submission-pdf-or-preview`,
        data
      );

      onCallback(true, res.data);
    } catch (err: any) {
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
      onCallback(false);
    }
  };

const SECTION_KEYS: (keyof IBusinessPolicyMarketingBinderPdf)[] = [
  "coverPage",
  "generalInformation",
  "priorPolicy",
  "underwriting",
  "claims",
  "riskLocation",
  "coverageSummary",
  "equipment",
  "toolFloater",
  "fineArt",
  "restaurantSupplement",
  "contractorSupplement",
  "schedules",
  "creditConsent",
  "coverageExplained",
  "coverageExclusions",
  "clientAcknowledgement",
  "clientDeclaration",
  "privacyConsent",
  "disclosure",
  "attachment",
];

const buildAaxelBinderSections = (
  data: IBusinessPolicyMarketingBinderPdf
): string[] =>
  SECTION_KEYS.filter((key) => Boolean(data[key])).map((key) => String(key));

export const aaxelBusinessBinderPreviewAsync =
  (
    data: IBusinessPolicyMarketingBinderPdf,
    onCallback: (isSuccess: boolean, html?: any) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch) => {
    try {
      const res = await api.post(
        `/policy/get-aaxel-business-fillable-binder-pdf`,
        {
          customer_policy_id: data.customer_policy_id,
          ...(data.lead_id ? { lead_id: data.lead_id } : {}),
          isBusinessBinderPreview: true,
          sections: buildAaxelBinderSections(data),
        }
      );
      onCallback(true, res.data);
    } catch (err: any) {
      dispatch(
        showMessage({
          type: "error",
          message: err.response?.data?.message ?? "Failed to preview binder.",
          displayAs: "snackbar",
        })
      );
      onCallback(false);
    }
  };

export const aaxelBusinessBinderGenerateAsync =
  (
    data: IBusinessPolicyMarketingBinderPdf,
    onCallback: (isSuccess: boolean, data?: any) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch) => {
    try {
      dispatch(saveLoaderProgress());
      const res = await api.post(
        `/policy/get-aaxel-business-fillable-binder-pdf`,
        {
          customer_policy_id: data.customer_policy_id,
          ...(data.lead_id ? { lead_id: data.lead_id } : {}),
          save: true,
          sections: buildAaxelBinderSections(data),
        }
      );
      onCallback(true, res?.data);
    } catch (err: any) {
      dispatch(
        showMessage({
          type: "error",
          message: err.response?.data?.message ?? "Failed to generate binder.",
          displayAs: "snackbar",
        })
      );
      onCallback(false);
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

export const businessPolicyGenerateMarketCoveragePdfAsync =
  (
    data: IBusinessPolicyMarketingBinderPdf,
    onCallback: (isSuccess: boolean, data?: any) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(saveLoaderProgress());
      const res = await api.post(
        `/policy/market-binder-submission-pdf-or-preview`,
        data
      );

      onCallback(true, res?.data);
      //dispatch(fetchBusinessMarketingAttachmentsAsync(data));
    } catch (err: any) {
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
      onCallback(false);
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

export const businessPolicySendCoverageMailAndHistAsync =
  (
    data: IBusinessPolicyMarketingBinderPdf,
    attachments: IFileUpload[],
    bccEmail: string,
    subject: string,
    text: string,
    underWriters: IInsurerUnderWriter[],
    onCallback: (isSuccess: boolean) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      const mailAttachments: { content: any; filename: string }[] = [];

      // Handle server-stored files (have path but no file)
      const serverKeys = attachments
        .filter((a) => a.path && !a.file)
        .map((a) => a.path as string);

      if (serverKeys.length > 0) {
        const bufferJson = await api.post("/general/download-files", {
          type: "json",
          keys: serverKeys,
        });
        bufferJson?.data?.forEach((ele: any, index: number) => {
          mailAttachments.push({
            content: ele,
            filename: serverKeys[index].split("/").at(-1) || "attachment",
          });
        });
      }

      // Handle locally uploaded files (have file object)
      const localFiles = attachments.filter((a) => a.file);
      for (const item of localFiles) {
        if (item.file) {
          const arrayBuffer = await item.file.arrayBuffer();
          const buffer = Buffer.from(arrayBuffer);
          mailAttachments.push({
            content: buffer as any,
            filename: item.name || item.file.name,
          });
        }
      }

      const mailPayload: IMail = {
        from_email: null,
        from_name: null,
        emails: [],
        bcc: bccEmail ? [bccEmail] : [],
        cc: [],
        subject: subject,
        body: text,
        templateName: null,
        templateId: null,
        objectVariables: {},
        attachments: mailAttachments,
      };

      const historyPayload: IBusinessPolicyMarketingHistory[] = [];

      // Upload / resolve the attachment paths once so every history record points
      // at the same stored files. Binder-generated files already carry a server
      // `path` (so uploadMultipleFile short-circuits for them), while locally added
      // files get uploaded and their returned paths are used.
      const { paths: attachmentPaths } = await uploadMultipleFile(
        attachments,
        "POLICY",
        {
          customer_id: data.customer_id ?? null,
          policy_type: "BUSINESS",
          customer_policy_id: data.customer_policy_id,
          page_name: "Marketing",
        }
      );
      const historyAttachments = attachmentPaths.filter(
        (path): path is string => Boolean(path)
      );

      for (const item of underWriters) {
        mailPayload.emails.push(item.email);
        historyPayload.push({
          customer_policy_id: data.customer_policy_id,
          quote_number: null,
          serial_number: null,
          premium: null,
          date_received: null,
          // Backend stores attachment as an array of string paths; the shared
          // type models it as IFileUpload[] so cast to keep runtime behavior
          // consistent with addBusinesPolicyMarketingHistAsync.
          attachment: historyAttachments as unknown as IFileUpload[],
          notes: null,
          status: "ACTIVE",
          underwriter: item.name,
          insurer: item.insurer_code,
          submission_date: item.insert_ts,
          effective_date: null,
          quote_status: "Submitted",
          customer_name: "",
          assigned_to_id: null,
          assigned_to_name: null,
        });
      }
      await api.post("/general/send-email", mailPayload);

      // Persist a marketing-history record for each selected underwriter. This is
      // what records the submission against the policy; without it only the email
      // and file-download calls fire and no marketing submission is saved.
      for (const payload of historyPayload) {
        await api.post(`/policy/edit-policy-add-marketing-history`, payload);
      }

      // Re-fetch the marketing-history list so the newly saved submission shows
      // up in the UI without a manual refresh.
      dispatch(
        fetchBusinessPolicyMarketingHistListAsync(data.customer_policy_id)
      );

      dispatch(
        showMessage({
          type: "success",
          message: "Email and Marketing history saved successfully!",
          displayAs: "snackbar",
        })
      );
      onCallback(true);
    } catch (err: any) {
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
      onCallback(false);
    }
  };

export const FETCH_BUSINESS_POLICY_MARKETING_HISTORY_ATTACHMENTS_PROGRESS =
  "FETCH_BUSINESS_POLICY_MARKETING_HISTORY_ATTACHMENTS_PROGRESS";
export const FETCH_BUSINESS_POLICY_MARKETING_HISTORY_ATTACHMENTS_SUCCESS =
  "FETCH_BUSINESS_POLICY_MARKETING_HISTORY_ATTACHMENTS_SUCCESS";
export const FETCH_BUSINESS_POLICY_MARKETING_HISTORY_ATTACHMENTS_FAILED =
  "FETCH_BUSINESS_POLICY_MARKETING_HISTORY_ATTACHMENTS_FAILED";

export const fetchBusinessMarketingAttachmentsProgress = () =>
  action(FETCH_BUSINESS_POLICY_MARKETING_HISTORY_ATTACHMENTS_PROGRESS);
export const fetchBusinessMarketingAttachmentsSuccesss = (
  data: IBusinessPolicyMatketingAttachment[]
) =>
  action(FETCH_BUSINESS_POLICY_MARKETING_HISTORY_ATTACHMENTS_SUCCESS, { data });

export const fetchBusinessMarketingAttachmentsFailed = () =>
  action(FETCH_BUSINESS_POLICY_MARKETING_HISTORY_ATTACHMENTS_FAILED);

export const fetchBusinessMarketingAttachmentsAsync =
  (
    payload: IBusinessPolicyMarketingBinderPdf
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(fetchBusinessMarketingAttachmentsProgress());
      const res = await api.get(
        `/customer/get-customer-marketing-history-details?customer_policy_id=${payload.customer_policy_id}`
      );
      const data: IBusinessPolicyMatketingAttachment[] = res.data.data;
      dispatch(
        fetchBusinessMarketingAttachmentsSuccesss(
          data.filter((x) => x.attachment !== null)
        )
      );
    } catch (err: any) {
      dispatch(fetchBusinessMarketingAttachmentsFailed());
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    }
  };

export const FETCH_BUSINESS_POLICY_MARKETING_HISTORY_LIST_PROGRESS =
  "FETCH_BUSINESS_POLICY_MARKETING_HISTORY_LIST_PROGRESS";
export const FETCH_BUSINESS_POLICY_MARKETING_HISTORY_LIST_SUCCESS =
  "FETCH_BUSINESS_POLICY_MARKETING_HISTORY_LIST_SUCCESS";
export const FETCH_BUSINESS_POLICY_MARKETING_HISTORY_LIST_FAILED =
  "FETCH_BUSINESS_POLICY_MARKETING_HISTORY_LIST_FAILED";

export const fetchBusinessPolicyMarketingHistListProgress = () =>
  action(FETCH_BUSINESS_POLICY_MARKETING_HISTORY_LIST_PROGRESS);
export const fetchBusinessPolicyMarketingHistListSuccess = (
  data: IBusinessPolicyMarketingHistory[]
) => action(FETCH_BUSINESS_POLICY_MARKETING_HISTORY_LIST_SUCCESS, { data });
export const fetchBusinessPolicyMarketingHistListFailed = () =>
  action(FETCH_BUSINESS_POLICY_MARKETING_HISTORY_LIST_FAILED);

export const fetchBusinessPolicyMarketingHistListAsync =
  (
    customerPolicyId: number,
    status?: string
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(fetchBusinessPolicyMarketingHistListProgress());
      let finalUrl = `/policy/get-marketing-history?customer_policy_id=${customerPolicyId}`;

      if (status !== undefined && status !== "-1") {
        finalUrl += `&status=${status}`;
      }
      const res = await api.get(finalUrl);
      const data: IBusinessPolicyMarketingHistory[] = res.data.data;
      dispatch(fetchBusinessPolicyMarketingHistListSuccess(data));
    } catch (err: any) {
      dispatch(fetchBusinessPolicyMarketingHistListFailed());
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    }
  };

// Feature: lead-binder-business-tabs
// Lead-scoped variant of fetchBusinessPolicyMarketingHistListAsync. Fetches the
// marketing-history list keyed by lead_id (never customer_policy_id) via the
// centralized endpoint map, so the Marketing binder sub-tab reads its records by the
// current lead (Requirements 3.1, 3.4). On failure or >30s timeout it flips the shared
// list load state to Failed (which the sub-tab surfaces as an error + retry) and shows
// a message (Requirement 3.5). Unsaved broker input lives in the form component, not
// this slice, so it is retained on failure. Mirrors the leadAutoApplicationActions
// precedent.
export const fetchLeadBusinessPolicyMarketingHistListAsync =
  (
    leadId: string,
    status?: string
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(fetchBusinessPolicyMarketingHistListProgress());

      const { url } = buildBinderRequest(
        "marketing",
        { kind: "lead", leadId },
        "fetch"
      );

      let finalUrl = url;
      if (status !== undefined && status !== "-1") {
        finalUrl += `&status=${status}`;
      }
      const res = await api.get(finalUrl, {
        timeout: LEAD_BINDER_REQUEST_TIMEOUT,
      });
      const data: IBusinessPolicyMarketingHistory[] = res.data.data;
      dispatch(fetchBusinessPolicyMarketingHistListSuccess(data));
    } catch (err: any) {
      dispatch(fetchBusinessPolicyMarketingHistListFailed());
      dispatch(
        showMessage({
          type: "error",
          message:
            err?.response?.data?.message ??
            "The Marketing binder section could not be loaded. Please retry.",
          displayAs: "snackbar",
        })
      );
    }
  };

export const FETCH_BUSINESS_POLICY_BINDER_LIST_PROGRESS =
  "FETCH_BUSINESS_POLICY_BINDER_LIST_PROGRESS";
export const FETCH_BUSINESS_POLICY_BINDER_LIST_SUCCESS =
  "FETCH_BUSINESS_POLICY_BINDER_LIST_SUCCESS";
export const FETCH_BUSINESS_POLICY_BINDER_LIST_FAILED =
  "FETCH_BUSINESS_POLICY_BINDER_LIST_FAILED";

export const fetchBusinessPolicyBindertListProgress = () =>
  action(FETCH_BUSINESS_POLICY_BINDER_LIST_PROGRESS);
export const fetchBusinessPolicyBindertListSuccess = (
  data: IBusinessBinder[]
) => action(FETCH_BUSINESS_POLICY_BINDER_LIST_SUCCESS, { data });
export const fetchBusinessPolicyBindertListFailed = () =>
  action(FETCH_BUSINESS_POLICY_BINDER_LIST_FAILED);

export const fetchBusinessPolicyBinderistListAsync =
  (
    customerPolicyId: number,
    status?: string
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(fetchBusinessPolicyBindertListProgress());

      let finalUrl = `/policy/get-binder-details?customer_policy_id=${customerPolicyId}`;

      if (status !== undefined && status !== "-1") {
        finalUrl += `&status=${status}`;
      }
      const res = await api.get(finalUrl);
      const data: IBusinessBinder[] = res.data.data;
      dispatch(fetchBusinessPolicyBindertListSuccess(data));
    } catch (err: any) {
      dispatch(fetchBusinessPolicyBindertListFailed());
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    }
  };

export const addBusinesPolicyMarketingHistAsync =
  (
    data: IBusinessPolicyMarketingHistory,
    ID: number,
    onCallback: (isSuccess: boolean) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(saveLoaderProgress());
      const asPayload = {
        customer_id: ID,
        policy_type: "BUSINESS",
        customer_policy_id: data.customer_policy_id,
        page_name: "Marketing",
      };
      const { paths } = await uploadMultipleFile(
        data.attachment,
        "POLICY",
        asPayload
      );
      const { paths: quotePaths } = await uploadMultipleFile(
        data.quote_attachment ?? [],
        "POLICY",
        asPayload
      );
      await api.post(`/policy/edit-policy-add-marketing-history`, {
        ...data,
        attachment: paths,
        quote_attachment: quotePaths,
      });
      dispatch(
        showMessage({
          type: "success",
          message: "Marketing History saved successfully!",
          displayAs: "snackbar",
        })
      );
      onCallback(true);
    } catch (err: any) {
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
      onCallback(false);
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

// Feature: lead-binder-business-tabs
// Lead-scoped variant of addBusinesPolicyMarketingHistAsync. Persists the
// marketing-history record keyed by lead_id and strips customer_policy_id/customer_id
// from the payload so a lead-scoped save never carries a customer_policy_id
// (Requirements 3.2, 3.4). Attachments are uploaded against the lead (page "Marketing")
// before the record is saved, mirroring the customer-policy upload flow. After a
// successful save it re-fetches the marketing-history list by the same lead_id so the
// sub-tab reflects the persisted records (Requirements 3.3, 3.7). A module-scoped guard
// plus saveLoaderProgress/saveLoaderCompleted prevent a duplicate in-flight save
// (Requirement 3.8). Failures / >30s timeouts surface a message and retain the broker's
// entered form input (the form owns its values, so nothing is discarded here)
// (Requirement 3.6).
export const upsertLeadBusinesPolicyMarketingHistAsync =
  (
    data: IBusinessPolicyMarketingHistory,
    leadId: string,
    onCallback: (isSuccess: boolean) => void,
    status?: string
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    if (isLeadMarketingSaveInProgress) {
      return;
    }
    isLeadMarketingSaveInProgress = true;
    try {
      dispatch(saveLoaderProgress());

      const asPayload = {
        policy_type: "BUSINESS",
        lead_id: leadId,
        page_name: "Marketing",
      };
      const { paths } = await uploadMultipleFile(
        data.attachment,
        "POLICY",
        asPayload
      );

      const { paths: quotePaths } = await uploadMultipleFile(
        data.quote_attachment ?? [],
        "POLICY",
        asPayload
      );

      const { url } = buildBinderRequest(
        "marketing",
        { kind: "lead", leadId },
        "save"
      );

      const { customer_policy_id, customer_id, ...rest } = data as any;
      await api.post(
        url,
        {
          ...rest,
          attachment: paths,
          quote_attachment: quotePaths,
          lead_id: leadId,
        },
        { timeout: LEAD_BINDER_REQUEST_TIMEOUT }
      );
      dispatch(
        showMessage({
          type: "success",
          message: "Marketing History saved successfully!",
          displayAs: "snackbar",
        })
      );
      // Re-fetch the marketing-history list by the same lead_id after a successful save
      // (Requirements 3.3, 3.7).
      dispatch(fetchLeadBusinessPolicyMarketingHistListAsync(leadId, status));
      onCallback(true);
    } catch (err: any) {
      dispatch(
        showMessage({
          type: "error",
          message:
            err?.response?.data?.message ??
            "The Marketing binder record could not be saved. Please retry.",
          displayAs: "snackbar",
        })
      );
      onCallback(false);
    } finally {
      isLeadMarketingSaveInProgress = false;
      dispatch(saveLoaderCompleted());
    }
  };

export const FETCH_BUSINESS_POLICY_MARKETING_HISTORY_PROGRESS =
  "FETCH_BUSINESS_POLICY_MARKETING_HISTORY_PROGRESS";
export const FETCH_BUSINESS_POLICY_MARKETING_HISTORY_SUCCESS =
  "FETCH_BUSINESS_POLICY_MARKETING_HISTORY_SUCCESS";
export const FETCH_BUSINESS_POLICY_MARKETING_HISTORY_FAILED =
  "FETCH_BUSINESS_POLICY_MARKETING_HISTORY_FAILED";

export const fetchBusinessPolicyMarketingHistProgress = () =>
  action(FETCH_BUSINESS_POLICY_MARKETING_HISTORY_PROGRESS);
export const fetchBusinessPolicyMarketingHistSuccess = (
  data: IBusinessPolicyMarketingHistory
) => action(FETCH_BUSINESS_POLICY_MARKETING_HISTORY_SUCCESS, { data });
export const fetchBusinessPolicyMarketingHistFailed = (errorMessage: string) =>
  action(FETCH_BUSINESS_POLICY_MARKETING_HISTORY_FAILED, { errorMessage });

export const fetchBusinessPolicyMarketingHistAsync =
  (
    customerPolicyId: number,
    marketingHistNumber: number
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(fetchBusinessPolicyMarketingHistProgress());
      const res = await api.get(
        `/policy/get-marketing-history?customer_policy_id=${customerPolicyId}&column=serial_number&value=${marketingHistNumber}`
      );
      const data: IBusinessPolicyMarketingHistory[] = res.data.data;
      if (data.length > 0) {
        const attachments: IFileUpload[] = [];
        for (const item of data[0].attachment as any) {
          attachments.push({
            file: null,
            key: getUniqueId(),
            path: item,
          });
        }
        const rawQuote = data[0].quote_attachment as any;
        const quoteAttachments: IFileUpload[] = (
          Array.isArray(rawQuote) ? rawQuote : rawQuote ? [rawQuote] : []
        ).map((item: string) => ({
          file: null,
          key: getUniqueId(),
          path: item,
        }));
        dispatch(
          fetchBusinessPolicyMarketingHistSuccess({
            ...data[0],
            attachment: attachments,
            quote_attachment: quoteAttachments,
          })
        );
      } else {
        dispatch(
          fetchBusinessPolicyMarketingHistFailed(
            "Oops! We couldn't find any records associated with your number at the moment. Please ensure that the provided information is accurate and try again."
          )
        );
      }
    } catch (err: any) {
      dispatch(
        fetchBusinessPolicyMarketingHistFailed(ERROR_MESSAGES.SERVER_ERROR)
      );
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    }
  };

// Feature: lead-binder-business-tabs
// Lead-scoped variant of fetchBusinessPolicyMarketingHistAsync. Loads a single
// marketing-history record for the edit form keyed by lead_id (never
// customer_policy_id) via the centralized endpoint map (Requirements 3.1, 3.4).
// Failures / >30s timeouts flip the marketing-history load state to Failed so the
// sub-tab surfaces an error + retry (Requirement 3.5).
export const fetchLeadBusinessPolicyMarketingHistAsync =
  (
    leadId: string,
    marketingHistNumber: number
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(fetchBusinessPolicyMarketingHistProgress());
      const { url } = buildBinderRequest(
        "marketing",
        { kind: "lead", leadId },
        "fetch"
      );
      const res = await api.get(
        `${url}&column=serial_number&value=${marketingHistNumber}`,
        { timeout: LEAD_BINDER_REQUEST_TIMEOUT }
      );
      const data: IBusinessPolicyMarketingHistory[] = res.data.data;
      if (data.length > 0) {
        const attachments: IFileUpload[] = [];
        for (const item of data[0].attachment as any) {
          attachments.push({
            file: null,
            key: getUniqueId(),
            path: item,
          });
        }
        const rawQuote = data[0].quote_attachment as any;
        const quoteAttachments: IFileUpload[] = (
          Array.isArray(rawQuote) ? rawQuote : rawQuote ? [rawQuote] : []
        ).map((item: string) => ({
          file: null,
          key: getUniqueId(),
          path: item,
        }));
        dispatch(
          fetchBusinessPolicyMarketingHistSuccess({
            ...data[0],
            attachment: attachments,
            quote_attachment: quoteAttachments,
          })
        );
      } else {
        dispatch(
          fetchBusinessPolicyMarketingHistFailed(
            "Oops! We couldn't find any records associated with your number at the moment. Please ensure that the provided information is accurate and try again."
          )
        );
      }
    } catch (err: any) {
      dispatch(
        fetchBusinessPolicyMarketingHistFailed(ERROR_MESSAGES.SERVER_ERROR)
      );
      dispatch(
        showMessage({
          type: "error",
          message:
            err?.response?.data?.message ??
            "The Marketing binder section could not be loaded. Please retry.",
          displayAs: "snackbar",
        })
      );
    }
  };

export const businessPolicyBinderSendCoverageMailAndHistAsync =
  (
    data: IBusinessPolicyMarketingBinderPdf,
    keys: Array<string>,
    bccEmail: string,
    subject: string,
    text: string,
    customerMail: string,
    onCallback: (isSuccess: boolean) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      const bufferJson = await api.post("/general/download-files", {
        type: "json",
        keys,
      });
      const mailPayload: IMail = {
        from_email: null,
        from_name: null,
        emails: [customerMail],
        bcc: bccEmail ? [bccEmail] : [],
        cc: [],
        subject: subject,
        body: text,
        templateName: null,
        templateId: null,
        objectVariables: {},
        attachments: bufferJson?.data?.map((ele: any, index: number) => ({
          content: ele,
          filename: keys[index].split("/").at(-1),
        })),
      };

      const historyPayload: IBusinessPolicyMarketingHistory[] = [];

      // mailPayload.attachments = attachmentResponse.data;
      // const a = Buffer.from(attachmentResponse.data);
      // console.log(a);

      await api.post("/general/send-email", mailPayload);

      for (const payload of historyPayload) {
        // await api.post(`/policy/edit-policy-add-marketing-history`, payload);
      }
      dispatch(
        showMessage({
          type: "success",
          message: "Email and Marketing history saved successfully!",
          displayAs: "snackbar",
        })
      );
      onCallback(true);
    } catch (err: any) {
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
      onCallback(false);
    }
  };

export const FETCH_CHECKLIST_SAVED_STATUS_PROGRESS =
  "FETCH_CHECKLIST_SAVED_STATUS_PROGRESS";
export const FETCH_CHECKLIST_SAVED_STATUS_SUCCESS =
  "FETCH_CHECKLIST_SAVED_STATUS_SUCCESS";
export const FETCH_CHECKLIST_SAVED_STATUS_FAILED =
  "FETCH_CHECKLIST_SAVED_STATUS_FAILED";

export const fetchChecklistSavedStatusProgress = () =>
  action(FETCH_CHECKLIST_SAVED_STATUS_PROGRESS);
export const fetchChecklistSavedStatusSuccess = (
  saved: boolean,
  response: IChecklistResponse | null
) => action(FETCH_CHECKLIST_SAVED_STATUS_SUCCESS, { saved, response });
export const fetchChecklistSavedStatusFailed = () =>
  action(FETCH_CHECKLIST_SAVED_STATUS_FAILED);

export const SAVE_CHECKLIST_RESPONSE_PROGRESS =
  "SAVE_CHECKLIST_RESPONSE_PROGRESS";
export const SAVE_CHECKLIST_RESPONSE_SUCCESS = "SAVE_CHECKLIST_RESPONSE_SUCCESS";
export const SAVE_CHECKLIST_RESPONSE_FAILED = "SAVE_CHECKLIST_RESPONSE_FAILED";

export const saveChecklistResponseProgress = () =>
  action(SAVE_CHECKLIST_RESPONSE_PROGRESS);
export const saveChecklistResponseSuccess = (response: IChecklistResponse) =>
  action(SAVE_CHECKLIST_RESPONSE_SUCCESS, { response });
export const saveChecklistResponseFailed = (errorMessage: string) =>
  action(SAVE_CHECKLIST_RESPONSE_FAILED, { errorMessage });

export const fetchChecklistSavedStatusAsync =
  (
    customerPolicyId: number | string,
    onComplete?: (fields: ITemplateField[] | null) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch) => {
    try {
      dispatch(fetchChecklistSavedStatusProgress());
      const res = await api.get(
        `/policy/get-policy-binder-checklist?customer_policy_id=${customerPolicyId}`
      );
      // API returns { data: [...] } — take the first record
      const list: IChecklistResponse[] = res.data.data ?? [];
      const record = list.length > 0 ? list[0] : null;
      if (record) {
        dispatch(fetchChecklistSavedStatusSuccess(true, record));
        onComplete?.(record.checklist ?? null);
      } else {
        dispatch(fetchChecklistSavedStatusSuccess(false, null));
        onComplete?.(null);
      }
    } catch (err: any) {
      if (err?.response?.status === 404) {
        dispatch(fetchChecklistSavedStatusSuccess(false, null));
        onComplete?.(null);
      } else {
        dispatch(fetchChecklistSavedStatusFailed());
        onComplete?.(null);
      }
    }
  };

export const saveChecklistResponseAsync =
  (
    customerId: number | string,
    customerPolicyId: number | string,
    fields: ITemplateField[],
    onSuccess: () => void,
    onError: (message: string) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(saveChecklistResponseProgress());
      // Send binder_checklist_code: null on first save (INSERT),
      // actual code on subsequent saves so backend performs UPDATE.
      const existingCode =
        getState().business.marketing.checklist.response
          ?.binder_checklist_code ?? null;
      const res = await api.post(`/policy/edit-policy-binder-checklist`, {
        binder_checklist_code: existingCode,
        customer_id: customerId,
        customer_policy_id: customerPolicyId,
        checklist: fields,
        status: "ACTIVE",
      });
      // Save response may also be an array — normalise to single record
      const responseData = res.data.data;
      const record = Array.isArray(responseData)
        ? responseData[0] ?? null
        : responseData ?? null;
      dispatch(saveChecklistResponseSuccess(record));
      onSuccess();
    } catch (err: any) {
      const message = err?.response?.data?.message ?? "An error occurred";
      dispatch(saveChecklistResponseFailed(message));
      onError(message);
    }
  };

export const CLEAR_BUSINESS_POLICY_MARKETING_STATE =
  "CLEAR_BUSINESS_POLICY_MARKETING_STATE";
export const CLEAR_BUSINESS_POLICY_MARKETING_HISTORY =
  "CLEAR_BUSINESS_POLICY_MARKETING_HISTORY";

export const clearBusinessPolicyMarketingState = () =>
  action(CLEAR_BUSINESS_POLICY_MARKETING_STATE);
export const clearBusinessPolicyMarketingHistory = () =>
  action(CLEAR_BUSINESS_POLICY_MARKETING_HISTORY);
